import { Button } from "@blueprintjs/core"
import { Dot, PlusCircle } from "@phosphor-icons/react"
import CustomCard from "components/Atoms/CustomCard"
import CustomDateRange from "components/Atoms/CustomDateRange"
import CustomTag from "components/Atoms/CustomTag"
import ReactTableHeader from "components/Common/ReactTableHeader"
import StaticSelector from "components/Atoms/StaticSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import TablePagination from "components/Molecules/TablePagination"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import SearchFilter, {
  SearchFilterCallout,
} from "components/Organisms/SearchFilter"
import dayjs from "dayjs"
import { useProductionsQuery } from "generated/graphql"
import { EXPORT_CUSTOMER, VARIETIES } from "graphql/selector.queries.gql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import React from "react"
import { useNavigate } from "react-router-dom"
import ReactTable from "react-table"
import { useFilter } from "store/search-filter/search-filter.reducer"
import ProductionActions from "../header/ProductionActions"
const PRODUCTION_STATUSES = [
  { id: "PENDING", name: "Pending" },
  { id: "SENT_FOR_APPROVAL", name: "Send For Approval" },
  { id: "VERIFIED", name: "Verified" },
  { id: "REJECTED", name: "Rejected" },
]

const Palletization = () => {
  const [{ page, search }, setFilter] = useSearchFilter({
    group: "*",
  })
  const navigate = useNavigate()
  const [filterProduction, setFilterProduction] = useFilter("production")

  const COLUMNS = [
    {
      Header: "Packing Date",
      accessor: "createdAt",
      Cell({ value, index }) {
        return (
          <div>
            <p className="text-center">
              {dayjs(value).format("DD MMM 'YY")}
              <br />
              {dayjs(value).format("hh:mm A")}
            </p>
          </div>
        )
      },
    },
    {
      Header: "W Ref. No",
      accessor: "weeklySrNo",
      Cell: ({ value }) => {
        return <p className="text-center">{value}</p>
      },
    },
    {
      Header: "Sales Order",
      accessor: "shipmentPlan.soNo",
      Cell({ value, original }) {
        return (
          <div
            onClick={() => {
              navigate(`${original.id}`)
            }}
            className="font-700 cursor-pointer"
          >
            <p className="text-center">{value}</p>
          </div>
        )
      },
    },

    {
      Header: "Assigned Supervisor",
      accessor: "supervisorNames",

      Cell({ value, original }) {
        return (
          <div className="flex flex-col items-center">
            {value.length > 0 ? (
              <OverFlowMenu
                icon="briefcase"
                className="w-40 flex items-center justify-center"
                items={
                  value?.map(atp => ({
                    id: atp ?? "",
                    name: atp ?? "--",
                  })) ?? []
                }
              />
            ) : (
              <>--</>
            )}

            <CustomTag intent="success" round>
              {original?.productionLine?.name}{" "}
            </CustomTag>
          </div>
        )
      },
    },
    {
      Header: "Customer Name",

      accessor: "shipmentPlan.exportCustomer.name",
      Cell({ value }) {
        return <p className="text-center">{value}</p>
      },
    },
    {
      Header: "Variety",
      accessor: "shipmentPlan.varieties",

      Cell({ value, original }) {
        return (
          <div className="flex flex-col items-center">
            <p className="text-center mb-0">
              {value?.map(item => item.name).join(", ")}
            </p>
            <CustomTag
              intent={
                original?.shipmentPlan?.varietyType === "SINGLE"
                  ? "warning"
                  : "primary"
              }
              round
              minimal
            >
              {original?.shipmentPlan?.varietyType}
            </CustomTag>
          </div>
        )
      },
    },
    {
      Header: "Packing (SKU)",
      accessor: "shipmentPlan.skuType.name",
      Cell({ value }) {
        return <p className="text-center">{value}</p>
      },
    },

    {
      Header: "MH Code",
      accessor: "certificateNumbers",
      Cell({ value }) {
        return (
          <OverFlowMenu
            icon="briefcase"
            className="w-40 flex items-center justify-center"
            items={
              value?.map(atp => ({
                id: atp ?? "",
                name: atp ?? "--",
              })) ?? []
            }
          />
        )
      },
    },
    {
      Header: "Gate Pass",
      accessor: "gatePasses",
      Cell({ value }) {
        return (
          <OverFlowMenu
            icon="briefcase"
            className="w-40 flex items-center justify-center"
            items={
              value?.map(atp => ({
                id: atp ?? "",
                name: atp ?? "--",
              })) ?? []
            }
          />
        )
      },
    },
    {
      Header: "Farmer Name",
      accessor: "farmerNames",
      Cell({ value }) {
        return (
          <OverFlowMenu
            icon="briefcase"
            className="w-40 flex items-center justify-center"
            items={
              value?.map(atp => ({
                id: atp ?? "",
                name: atp ?? "--",
              })) ?? []
            }
          />
        )
      },
    },
    {
      Header: "Status",
      accessor: "sendForApproval",
      Cell({ value, original }) {
        let statusText = ""
        let colorClass = ""
        const metadata = original?.metadata
          ? JSON.parse(original.metadata)
          : null
        const status = metadata?.status

        // Priority: verify > sendForApproval > reject > pending
        if (status === "verify") {
          statusText = "Verified"
          colorClass = "text-green-500"
        } else if (value) {
          statusText = "Send For Approval"
          colorClass = "text-blue-400"
        } else if (status === "reject") {
          statusText = "Rejected"
          colorClass = "text-red-500"
        } else {
          statusText = "Pending"
          colorClass = "text-yellow-500"
        }

        return (
          <span
            className={`${colorClass} font-500 text-xs text-center flex items-center`}
          >
            <Dot weight="fill" size={20} /> {statusText}
          </span>
        )
      },
    },
  ]

  const {
    data: iData,
    loading,
    error,
  } = useProductionsQuery({
    variables: {
      search: search ?? "",
      limit: 10,
      offset: page,
      exportCustomerId: filterProduction?.exportCustomer?.id,
      varietyId: filterProduction?.variety?.id,
      fromDate: filterProduction?.createdAtRange?.[0]
        ? dayjs(filterProduction.createdAtRange[0]).format("YYYY-MM-DD")
        : undefined,
      toDate: filterProduction?.createdAtRange?.[1]
        ? dayjs(filterProduction.createdAtRange[1]).format("YYYY-MM-DD")
        : undefined,
      productionStatus: filterProduction?.productionStatus?.id ?? undefined,
    },
    fetchPolicy: "network-only",
  })
  const list = iData?.productions ?? []
  if (error) return <>{`Errors ${error.message}`}</>

  return (
    <CustomCard className="p-0">
      <ReactTableHeader
        icon="stacked-chart"
        title="Palletization"
        value={search ?? ""}
        searchPlaceHolder="Search by So Number, W Ref. No."
        onQueryChange={search => {
          setFilter(prev => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
        filter={
          <SearchFilter
            value={filterProduction}
            className="ml-3"
            getValues={setFilterProduction}
            onClear={() => setFilterProduction({})}
          >
            <SearchFilterCallout title="Variety" fill>
              <DynamicSelector
                fill
                big
                formClassName="col-span-2"
                query={VARIETIES}
                variables={{
                  includeCommodity: false,
                }}
                path="variety"
                value={filterProduction?.variety}
                onChange={(_path, value) => {
                  setFilterProduction(prev => ({
                    ...prev,
                    variety: value,
                  }))
                }}
              />
            </SearchFilterCallout>
            <SearchFilterCallout title="Customer" fill>
              <DynamicSelector
                fill
                big
                formClassName="col-span-2"
                query={EXPORT_CUSTOMER}
                variables={{
                  includeCommodity: false,
                }}
                path="exportCustomer"
                value={filterProduction?.exportCustomer}
                onChange={(_path, value) => {
                  setFilterProduction(prev => ({
                    ...prev,
                    exportCustomer: value,
                  }))
                }}
              />
            </SearchFilterCallout>
            <SearchFilterCallout title="Production Status" fill>
              <StaticSelector
                fill
                big
                nullable
                items={PRODUCTION_STATUSES}
                path="productionStatus"
                value={filterProduction?.productionStatus}
                renderText={item => item?.name ?? "Select Status"}
                onChange={(_path, value) => {
                  setFilterProduction(prev => ({
                    ...prev,
                    productionStatus: value?.id ? value : undefined,
                  }))
                }}
              />
            </SearchFilterCallout>
            <SearchFilterCallout title="Created At" fill>
              <CustomDateRange
                path="createdAtRange"
                big={false}
                shortcuts={true}
                contiguousCalendarMonths={false}
                value={filterProduction?.createdAtRange}
                onChange={(_path, value) => {
                  setFilterProduction(prev => ({
                    ...prev,
                    createdAtRange: value,
                  }))
                }}
              />
            </SearchFilterCallout>
          </SearchFilter>
        }
      >
        <TablePagination
          disabledNext={iData?.productions.length < 10}
          className="mx-3"
          page={page}
          onPageChange={page => {
            setFilter(prev => ({
              ...prev,
              page,
            }))
          }}
        />
        <div className="flex items-center gap-2">
          <ProductionActions />
          <Button
            className="flex items-center bg-[#293742] text-white rounded"
            icon={<PlusCircle color="white" size={18} />}
            text="Sales Order"
            intent="primary"
            minimal
            onClick={() => navigate("new")}
          />
        </div>
      </ReactTableHeader>

      <ReactTable
        className="mt-0 vt-master-table vt_user_table"
        loading={loading}
        pageSize={10}
        showPagination={false}
        resizable={true}
        data={list}
        columns={COLUMNS}
      />
    </CustomCard>
  )
}
export default Palletization
